/**
 * Invitation Type Add/Edit form (upsertInvitationType, POST).
 *
 * - Create: name, label, description, targetKind. New types always start `inactive`
 *   (the server ignores status on create), so no status control is shown.
 * - Edit: name + targetKind read-only; status active/inactive, with Activate disabled while
 *   no handler is deployed. Deactivating asks for confirmation.
 * - Archived types open read-only (the server rejects changes with 409 INVITATION_TYPE_ARCHIVED).
 */

import * as React from 'react'
import { z } from 'zod'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/contexts/ToastContext'
import { AppLogger } from '@/lib/logger'
import { ArrowLeft, MailPlus } from 'lucide-react'
import type { InvitationType } from '@/types/users'

const log = AppLogger.getLogger(import.meta)

const invitationTypeFormSchema = z.object({
  invitationTypeName: z.string().min(1, 'Name is required').max(100)
    .regex(/^[a-z][A-Za-z0-9]*$/, 'camelCase: start with a lowercase letter, letters and digits only'),
  invitationTypeLabel: z.string().trim().min(1, 'Label is required').max(150),
  invitationTypeDescription: z.string().max(500, 'Description cannot exceed 500 characters').optional(),
  targetKind: z.enum(['organization', 'none']),
})

export interface InvitationTypeFormProps {
  initial?: InvitationType
  onSuccess: () => void
  onCancel: () => void
}

const fmt = (s?: number | null) => (s ? new Date(s * 1000).toLocaleDateString() : '—')

export function InvitationTypeForm({ initial, onSuccess, onCancel }: InvitationTypeFormProps) {
  const { toast } = useToast()
  const isEdit = !!initial
  const archived = !!initial?.archiveDate

  const [name, setName] = React.useState(initial?.invitationTypeName ?? '')
  const [label, setLabel] = React.useState(initial?.invitationTypeLabel ?? '')
  const [description, setDescription] = React.useState(initial?.invitationTypeDescription ?? '')
  const [targetKind, setTargetKind] = React.useState<'organization' | 'none'>(initial?.targetKind ?? 'organization')
  const [status, setStatus] = React.useState<string>(initial?.status ?? 'inactive')
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [confirmInactive, setConfirmInactive] = React.useState(false)

  const upsert = useApiMutation('upsertInvitationType', 'POST', { invalidates: ['getInvitationTypes', 'getInvitationType'] })

  const doSubmit = async () => {
    setErrors({})
    const parsed = invitationTypeFormSchema.safeParse({
      invitationTypeName: name, invitationTypeLabel: label, invitationTypeDescription: description || undefined, targetKind,
    })
    if (!parsed.success) {
      const next: Record<string, string> = {}
      for (const issue of parsed.error.issues) next[String(issue.path[0])] ??= issue.message
      setErrors(next)
      return
    }
    const payload = {
      ...parsed.data,
      invitationTypeDescription: parsed.data.invitationTypeDescription ?? null,
      ...(isEdit && { invitationTypeNumber: initial!.invitationTypeNumber, status }),
    }
    try {
      await upsert.mutateAsync(payload)
      toast({
        title: isEdit ? 'Invitation type updated' : 'Invitation type created',
        description: isEdit ? undefined : 'New types start inactive — activate it once its handler is deployed.',
        type: 'success',
      })
      onSuccess()
    } catch (err) {
      const msg = (err as Error)?.message ?? ''
      log.error('upsertInvitationType failed', err)
      if (/already exists|NAME_TAKEN/i.test(msg)) setErrors({ invitationTypeName: 'This name is taken (names are never reused, even archived ones).' })
      else if (/event schema|NOT_IN_EVENT_SCHEMA/i.test(msg)) {
        toast({
          title: 'Can’t activate yet',
          description: 'Add this name to the invitationAdded event schema’s invitationType enum first (Event Types screen).',
          type: 'error', duration: 8000,
        })
      } else toast({ title: isEdit ? 'Could not update invitation type' : 'Could not create invitation type', description: msg || 'Please try again.', type: 'error' })
    }
  }

  const handleSubmit = () => {
    if (isEdit && initial?.status === 'active' && status === 'inactive') return setConfirmInactive(true)
    doSubmit()
  }

  const err = (k: string) => errors[k] && <p className="text-sm text-destructive">{errors[k]}</p>
  const readOnly = archived

  return (
    <div className="space-y-4">
      <Button variant="ghost" className="gap-2" onClick={onCancel}><ArrowLeft className="h-4 w-4" />Back to Invitation Types</Button>

      <Card className="shadow-sm pb-0 overflow-hidden">
        <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><MailPlus className="h-5 w-5 text-primary" /></div>
            <div>
              <CardTitle className="text-lg">{archived ? 'Invitation Type (archived)' : isEdit ? 'Edit Invitation Type' : 'Add Invitation Type'}</CardTitle>
              <CardDescription>The reason someone is invited — decides the email they get and what accepting does.</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="space-y-6 pt-6">
          {isEdit && initial && (
            <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/20 p-4 text-sm md:grid-cols-5">
              <div><p className="text-xs text-muted-foreground">Number</p><p className="font-mono">{initial.invitationTypeNumber}</p></div>
              <div><p className="text-xs text-muted-foreground">Handler</p><p>{initial.hasHandler ? 'Deployed' : 'Not deployed'}</p></div>
              <div><p className="text-xs text-muted-foreground">Pending invitations</p><p>{initial.pendingInvitationCount ?? 0}</p></div>
              <div><p className="text-xs text-muted-foreground">Created</p><p>{fmt(initial.createdAt)}</p></div>
              <div><p className="text-xs text-muted-foreground">{archived ? 'Archived' : 'Updated'}</p><p>{fmt(archived ? initial.archiveDate : initial.updatedAt)}</p></div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Name <span className="text-destructive">*</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={isEdit} className="font-mono" placeholder="farmAdminInvitedToFarmgrid" />
            <p className="text-xs text-muted-foreground">camelCase, unique, never reused{isEdit ? ' — locked after create' : ''}.</p>{err('invitationTypeName')}
          </div>
          <div className="space-y-2">
            <Label>Label <span className="text-destructive">*</span></Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} disabled={readOnly} placeholder="Farm admin invited to FarmGrid" />{err('invitationTypeLabel')}
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} disabled={readOnly} placeholder="Shown as helper text in the Invite as dropdown" />{err('invitationTypeDescription')}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Target kind <span className="text-destructive">*</span></Label>
              <Select value={targetKind} onValueChange={(v) => setTargetKind(v as 'organization' | 'none')} disabled={isEdit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="organization">Organization</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">What the person is invited to{isEdit ? ' — locked after create' : ''}.</p>
            </div>
            {isEdit && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus} disabled={readOnly}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active" disabled={!initial?.hasHandler && initial?.status !== 'active'}>
                      {initial?.hasHandler || initial?.status === 'active' ? 'Active' : 'Active — no handler deployed for this type yet'}
                    </SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && <Button onClick={handleSubmit} disabled={upsert.isPending}>{upsert.isPending ? 'Saving...' : isEdit ? 'Save' : 'Create'}</Button>}
      </div>

      <Dialog open={confirmInactive} onOpenChange={setConfirmInactive}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Deactivate this invitation type?</DialogTitle>
            <DialogDescription>
              It will disappear from the Invite as dropdown. Invitations already sent ({initial?.pendingInvitationCount ?? 0} pending) aren't affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmInactive(false)}>Cancel</Button>
            <Button onClick={() => { setConfirmInactive(false); doSubmit() }}>Deactivate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
